import { useEffect, useState, useCallback } from 'react';
import api from './axiosSetup';
import CustomNavbar from './CustomNavbar';
import cfg from '../config';

const formatDate = (d) => {
  if (!d) return 'Kein Datum';
  const date = new Date(d);
  if (isNaN(date.getTime())) return 'Kein Datum';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDt = (dt) =>
  dt ? new Date(dt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '–';

const isOverdue = (dueDate, status) => {
  if (!dueDate || status === 'erledigt') return false;
  return new Date(dueDate) < new Date();
};

// ── Stil-Konstanten ──────────────────────────────────────────
const S = {
  card:       { background: '#1e2130', border: '1px solid #2e3347', borderRadius: 10 },
  cardHeader: { background: '#252839', borderBottom: '1px solid #2e3347', padding: '0.9rem 1.2rem', borderRadius: '10px 10px 0 0' },
  muted:      { color: '#7b82a0', fontSize: '0.82rem' },
  input:      { background: '#13151f', border: '1px solid #3a3f55', color: '#e8eaf0', borderRadius: 6 },
  badge: {
    hoch:           { background: '#3a1a1a', color: '#f87171', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20 },
    mittel:         { background: '#2a1f0a', color: '#fbbf24', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20 },
    niedrig:        { background: '#0a2a0a', color: '#4ade80', fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20 },
  },
};

const PriorityBadge = ({ priority }) => {
  const style = S.badge[priority] || S.badge.mittel;
  return <span style={style}>{priority}</span>;
};

// ── Haupt-Seite ──────────────────────────────────────────────
const MyTasks = () => {
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('offen');
  const [statusChanging, setStatusChanging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/tasks');
      setTasks(res.data || []);
    } catch {
      setError('Fehler beim Laden deiner Aufgaben.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (taskId, newStatus) => {
    setStatusChanging(taskId);
    try {
      await api.patch(`/api/tasks/${taskId}/status`, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      setError(err.response?.data?.message || 'Fehler beim Ändern des Status.');
    } finally {
      setStatusChanging(null);
    }
  };

  const tabs = [
    { key: 'offen',         label: 'Offen' },
    { key: 'in Bearbeitung', label: 'In Bearbeitung' },
    { key: 'erledigt',       label: 'Erledigt' },
  ];

  const shown = tasks.filter(t => t.status === tab);

  const emptyMsg = {
    'offen':          'Keine offenen Aufgaben.',
    'in Bearbeitung': 'Keine Aufgaben in Bearbeitung.',
    'erledigt':       'Noch keine erledigten Aufgaben.',
  };

  const tabStyle = (key) => ({
    background:   tab === key ? '#ffc107' : 'none',
    color:        tab === key ? '#000' : '#7b82a0',
    border:       `1px solid ${tab === key ? 'transparent' : '#2e3347'}`,
    borderRadius: 20,
    padding:      '5px 16px',
    cursor:       'pointer',
    fontWeight:   tab === key ? 600 : 400,
    fontSize:     '0.88rem',
    transition:   'all 0.15s ease',
    whiteSpace:   'nowrap',
  });

  return (
    <div style={{ minHeight: '100vh', background: '#0f1117' }}>
      <CustomNavbar />
      <div className="container mt-4 pb-5" style={{ maxWidth: 700 }}>

        <h2 style={{ color: '#e8eaf0', fontWeight: 700, marginBottom: 2 }}>Meine Aufgaben</h2>
        <p style={{ ...S.muted, marginBottom: 20 }}>Dir zugewiesene Aufgaben von {cfg.adminRoleName}</p>

        {error && <div className="alert alert-danger d-flex justify-content-between align-items-center py-2">{error}<button className="btn-close btn-close-white btn-sm" onClick={() => setError(null)} /></div>}

        {/* ── Tabs ── */}
        <div className="d-flex gap-2 mb-4 flex-wrap">
          {tabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={tabStyle(key)}
            >
              {label} ({tasks.filter(t => t.status === key).length})
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" style={{ color: '#ffc107' }} />
          </div>
        ) : shown.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#4a5068', padding: '60px 0' }}>
            {emptyMsg[tab]}
          </div>
        ) : (
          shown.map(task => {
            const overdue = isOverdue(task.dueDate, task.status);
            return (
              <div key={task.id} style={{ ...S.card, marginBottom: 12 }}>
                <div style={{ padding: '1rem 1.2rem' }}>
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

                    {/* ── Linke Seite ── */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <span style={{ color: '#e8eaf0', fontWeight: 600, fontSize: '0.97rem' }}>{task.title}</span>
                        <PriorityBadge priority={task.priority} />
                      </div>
                      {task.description && (
                        <p style={{ color: '#9aa3bc', fontSize: '0.88rem', marginBottom: 6 }}>{task.description}</p>
                      )}
                      <div className="d-flex flex-wrap gap-3 mt-1">
                        <span style={{ ...S.muted, color: overdue ? '#f87171' : '#7b82a0' }}>
                          Fällig: {formatDate(task.dueDate)}{overdue && ' ⚠ Überfällig!'}
                        </span>
                        <span style={S.muted}>Von: {task.createdByName}</span>
                        <span style={S.muted}>Erstellt: {formatDt(task.createdAt)}</span>
                      </div>
                      {overdue && (
                        <div style={{ marginTop: 8, background: '#2a1010', border: '1px solid #5a2020', borderRadius: 6, padding: '6px 12px' }}>
                          <span style={{ color: '#f87171', fontSize: '0.82rem', fontWeight: 600 }}>
                            Diese Aufgabe ist überfällig!
                          </span>
                        </div>
                      )}
                    </div>

                    {/* ── Rechte Seite: Status-Änderung ── */}
                    <div style={{ flexShrink: 0 }}>
                      <label style={{ ...S.muted, display: 'block', marginBottom: 4 }}>Status</label>
                      <select
                        value={task.status}
                        onChange={e => handleStatusChange(task.id, e.target.value)}
                        disabled={statusChanging === task.id}
                        style={{ ...S.input, padding: '5px 10px', fontSize: '0.85rem', cursor: 'pointer' }}
                      >
                        <option value="offen">Offen</option>
                        <option value="in Bearbeitung">In Bearbeitung</option>
                        <option value="erledigt">Erledigt</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MyTasks;
